import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { BrowserTracing } from '@sentry/tracing'
import VueApollo from 'vue-apollo'
import { BootstrapVue } from 'bootstrap-vue'
import { ApolloClient } from 'apollo-client'
import { createHttpLink } from 'apollo-link-http'
import { InMemoryCache } from 'apollo-cache-inmemory'
import {truncate, startCase} from 'lodash'
import cropUrl from 'crop-url'

import {
  BIconArrowClockwise,
  BIconArrowDown,
  BIconArrowRightCircle,
  BIconArrowRightShort,
  BIconBoxArrowUpRight,
  BIconCaretDownSquareFill,
  BIconCaretUpSquareFill,
  BIconCheckCircle,
  BIconCheckCircleFill,
  BIconChevronDown,
  BIconChevronUp,
  BIconClock,
  BIconDashCircle,
  BIconExclamationSquare,
  BIconExclamationTriangle,
  BIconExclamationTriangleFill,
  BIconFileBarGraph,
  BIconFileEarmarkCode,
  BIconFiles,
  BIconFunnel,
  BIconGeoAltFill,
  BIconImage,
  BIconInfoCircle,
  BIconKanban,
  BIconList,
  BIconLockFill,
  BIconPencil,
  BIconPerson,
  BIconPlusCircle,
  BIconQuestionCircle,
  BIconRecordFill,
  BIconSearch,
  BIconSliders,
  BIconTable,
  BIconTrash,
  BIconX,
  BIconXCircle,
  BIconXCircleFill,
  BIconMenuButtonWideFill,
  BIconXSquare,
  BIconTextIndentLeft,
} from 'bootstrap-vue'

Vue.component('BIconArrowClockwise', BIconArrowClockwise)
Vue.component('BIconArrowDown', BIconArrowDown)
Vue.component('BIconArrowRightCircle', BIconArrowRightCircle)
Vue.component('BIconArrowRightShort', BIconArrowRightShort)
Vue.component('BIconBoxArrowUpRight', BIconBoxArrowUpRight)
Vue.component('BIconCaretDownSquareFill', BIconCaretDownSquareFill)
Vue.component('BIconCaretUpSquareFill', BIconCaretUpSquareFill)
Vue.component('BIconCheckCircle', BIconCheckCircle)
Vue.component('BIconCheckCircleFill', BIconCheckCircleFill)
Vue.component('BIconChevronDown', BIconChevronDown)
Vue.component('BIconChevronUp', BIconChevronUp)
Vue.component('BIconClock', BIconClock)
Vue.component('BIconDashCircle', BIconDashCircle)
Vue.component('BIconExclamationSquare', BIconExclamationSquare)
Vue.component('BIconExclamationTriangle', BIconExclamationTriangle)
Vue.component('BIconExclamationTriangleFill', BIconExclamationTriangleFill)
Vue.component('BIconFileBarGraph', BIconFileBarGraph)
Vue.component('BIconFileEarmarkCode', BIconFileEarmarkCode)
Vue.component('BIconFiles', BIconFiles)
Vue.component('BIconFunnel', BIconFunnel)
Vue.component('BIconGeoAltFill', BIconGeoAltFill)
Vue.component('BIconImage', BIconImage)
Vue.component('BIconInfoCircle', BIconInfoCircle)
Vue.component('BIconKanban', BIconKanban)
Vue.component('BIconList', BIconList)
Vue.component('BIconLockFill', BIconLockFill)
Vue.component('BIconPencil', BIconPencil)
Vue.component('BIconPerson', BIconPerson)
Vue.component('BIconPlusCircle', BIconPlusCircle)
Vue.component('BIconQuestionCircle', BIconQuestionCircle)
Vue.component('BIconRecordFill', BIconRecordFill)
Vue.component('BIconSearch', BIconSearch)
Vue.component('BIconSliders', BIconSliders)
Vue.component('BIconTable', BIconTable)
Vue.component('BIconTrash', BIconTrash)
Vue.component('BIconX', BIconX)
Vue.component('BIconXCircle', BIconXCircle)
Vue.component('BIconXCircleFill', BIconXCircleFill)
Vue.component('BIconMenuButtonWideFill', BIconMenuButtonWideFill)
Vue.component('BIconXSquare', BIconXSquare)
Vue.component('BIconTextIndentLeft', BIconTextIndentLeft)

Sentry.init({
  Vue: Vue,
  dsn: 'https://336143895d0b4f74a52d70a3401dc204@o916949.ingest.sentry.io/5858872',
  release: process.env.VUE_APP_VERSION, 
  environment: process.env.NODE_ENV,
  integrations: [
    new BrowserTracing({
      // routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ['localhost', 'zyppy.com', 'siteseer.io'],
      tracesSampleRate: process.env.NODE_ENV == 'production' ? 0.1 : 1.0,
    }),
  ],
})

// Used by Segment/Sentry
window.VUE_APP_VERSION = process.env.VUE_APP_VERSION

Vue.prototype.$apolloProvider = new VueApollo({
  defaultClient: new ApolloClient({
    // connectToDevTools: true,
    link: createHttpLink({
      uri: (window.location.hostname == 'staging.siteseer.io' ? 'https://siteseer.io' : '') + '/v1/graphql',
      credentials: 'include',
    }),
    cache: new InMemoryCache(),
  }),
  defaultOptions: {
    $query: {
      fetchPolicy: 'cache-and-network'
    }
  }
})

Vue.config.productionTip = false
Vue.use(VueApollo)
Vue.use(BootstrapVue)

const formatNumber = (num, signDisplay = false) => {
  const options = {}
  if(signDisplay === true) {
    options.signDisplay = 'exceptZero'
  }
  if(num === null || isNaN(num)) {
    return '-'; //&nbsp;
  }
  const locale = Intl.NumberFormat().resolvedOptions().locale
  return Intl.NumberFormat(locale, options).format(Number(num).toFixed())
}
Vue.filter('formatNumber', formatNumber)

// @see https://gist.github.com/tobyjsullivan/96d37ca0216adee20fa95fe1c3eb56ac
Vue.filter('abbreviateNumber', value => {
  let newValue = value;
  const suffixes = ["", "K", "M", "B","T"];
  let suffixNum = 0;
  while (newValue >= 1000) {
    newValue /= 1000;
    suffixNum++;
  }

  newValue = Math.round(newValue);

  newValue += suffixes[suffixNum];
  return newValue;
})
Vue.filter('toMonthly', num => Math.round(num / (90/28)))
Vue.filter('formatDate', (date) => new Date(date).toLocaleDateString())
Vue.filter('formatTime', (date) => new Date(date).toLocaleTimeString())
Vue.filter('daysSince', (date, date2 = new Date()) => Math.ceil((date2.getTime() - new Date(date).getTime()) / (1000 * 3600 * 24)))
Vue.filter('titleCase', startCase)
Vue.filter('truncate', (str, length=90) => truncate(str, {length}))
Vue.filter('truncateUrl', (str, length=90) => cropUrl(str, length))
Vue.filter('relativeTime', e => {e=new Date(e).getTime()-new Date().getTime();for(let[u,a]of Object.entries({year:31536e6,month:2628e6,day:864e5,hour:36e5,minute:6e4,second:1e3})){if(Math.abs(e)>a||a===1e3){return new Intl.RelativeTimeFormat('en',{style:'narrow'}).format(~~(e/a),u)}}})
Vue.filter('delta', function(a, b) {
  const val = (a - b) / a * 100
  if(isNaN(val)) {
    return 0
  }
  switch(val) {
    case Number.POSITIVE_INFINITY:
      return 100
    case Number.NEGATIVE_INFINITY:
      return -100
    default:
      return Math.round(val)
  }
})
Vue.filter('daysAgo', (days, ts) => {
  const date = ts ? new Date(ts) : new Date()
  date.setDate(date.getDate() - days)
  date.setHours(0)
  date.setSeconds(0)
  date.setMilliseconds(0)
  return date
})
Vue.filter('pluralize', (noun, count, suffix = 's', showCount = true) => 
  `${showCount ? formatNumber(count)+' ' : ''}${noun}${Number(count) !== 1 ? suffix : ''}`
)
// @see https://stackoverflow.com/a/50579690
Vue.filter('crc32', r => {for(var a,o=[],c=0;c<256;c++){a=c;for(var f=0;f<8;f++)a=1&a?3988292384^a>>>1:a>>>1;o[c]=a}for(var n=-1,t=0;t<r.length;t++)n=n>>>8^o[255&(n^r.charCodeAt(t))];return(-1^n)>>>0})

Vue.config.keyCodes.s = 83

Vue.prototype.$loadScript = function(url) {
  return new Promise((resolve) => {
    let script = document.createElement('script')
    script.onload = () => {
      resolve()
    }
    script.async = true
    script.src = url
    document.head.appendChild(script)
  })
}

export default Vue
